// Summoning Sickness + Charge. Pure. No React, no Three.js.
//
// ── Codex anchors ──
//
//   Tap. "A minion with summoning sickness cannot tap or be tapped
//     to pay costs (but may be tapped directly by other effects,
//     e.g. Grievous Insult)."
//   Charge. "Keyword Ability. This minion does not suffer from
//     summoning sickness. It can tap or be tapped to pay for costs
//     associated with an ability the same turn it is summoned."
//   Summoning Sickness. "until the end of turn." Sickness clears at
//     the NEXT turn boundary for BOTH players' minions (the untap_all
//     step / server clearThisTurnEffects). A minion summoned on your
//     turn "may defend on your opponent's turn" (codex), so the flag
//     must not survive into the opponent's turn.
//     Charge only ever does work on the summon turn. Once
//     sickness would have lifted anyway, Charge has no remaining
//     effect, so we sunset the printed keyword at the same trigger
//     to keep the on-card badge honest about what's actually doing
//     anything for the player.
//
// ── State model ──
//
//   `cardInstance._hasSummoningSickness: boolean`. Set on fresh-play
//   commit (unless Charge), cleared for every minion at the next turn
//   boundary (untap-all phase step). No turn-number bookkeeping
//   needed. The flag itself encodes "still sick".
//
//   `cardInstance._chargeFresh: boolean`. Set on fresh-play commit
//   for any Minion that entered with Charge. Drained by
//   expireFreshCharge in the same untap_all step that clears sickness;
//   when drained, the keyword is removed from `_printedKeywords` so
//   the badge and the engine both stop reporting Charge after it has
//   served its only purpose. Aura-granted Charge is unaffected
//   (lives in `_auraStatuses`, recomputed each pass).
//
// ── Cost-paying vs effect-driven taps ──
//
//   Cost-paying tap sources (block when sick):
//     'activate'. Activated-ability tap-cost
//     'move'. Auto-tap on cell-cross (part of Move and Attack)
//     'attack'. Auto-tap on attack-only (part of Move and Attack)
//
//   Effect-driven / non-cost tap sources (always allowed):
//     'effect'. Card effects that tap targets (Grievous Insult)
//     'remote'. Multiplayer sync (peer already enforced policy)
//     'end_turn'. Bulk untap (not a tap)
//     'site_play_shortcut'. Avatar tap; Avatars never have sickness
//     'action_menu', 'hotkey'. User override, permissive for sandbox
//
// ── Applies only to Minions ──
//
// Per codex, sickness applies specifically to minions. Avatars never
// suffer (they're already in play at game start, plus transform rules
// exempt them). Sites and artifacts don't act, so the question doesn't
// arise. markSummonedThisTurn is a no-op for non-Minions.

import { hasKeyword } from './keywordQuery';
import { countsAsMinion } from './typeline';

const COST_PAYING_TAP_SOURCES = new Set(['activate', 'move', 'attack']);

export function hasCharge(card) {
  return hasKeyword(card, 'charge');
}

export function hasSummoningSickness(card) {
  if (!card) return false;
  // Effective read: codex Charge says the minion "does not suffer from
  // summoning sickness", and charge can arrive AFTER the stamp (a static
  // aura like Horn of Caerleon, or a summon INTO its range). The stamp only
  // skips printed charge, so every gate must re-check the keyword here.
  // Reading the raw flag made aura-granted Charge purely cosmetic.
  return !!card._hasSummoningSickness && !hasCharge(card);
}

/**
 * Mark a card as freshly summoned. No-op for non-Minions. Per codex
 * Charge bypasses sickness entirely, so no `_hasSummoningSickness`
 * flag is set on Charge units. But we DO set `_chargeFresh` so the
 * untap step can sunset the now-spent keyword.
 *
 * `summonedTurnKey` (turnKey() from controlChange.js) stamps WHICH turn
 * the minion entered so a later control change can tell genuine sickness
 * from a stale flag the original controller's untap hasn't cleared yet.
 * Optional so existing callers and tests stay valid.
 */
export function markSummonedThisTurn(card, summonedTurnKey) {
  if (!card) return;
  // countsAsMinion: Automatons enter the realm as minions (codex).
  // they arrive sick like any other summon.
  if (!countsAsMinion(card)) return;
  if (summonedTurnKey !== undefined) card._summonedOnTurnKey = summonedTurnKey;
  if (hasCharge(card)) {
    card._chargeFresh = true;
    return;
  }
  card._hasSummoningSickness = true;
}

/**
 * Clear the sickness flag. Called by the start-phase untap step for
 * every minion on the board (sickness is turn-scoped, not
 * controller-turn-scoped).
 */
export function clearSummoningSickness(card) {
  if (!card) return;
  if (card._hasSummoningSickness) delete card._hasSummoningSickness;
}

/**
 * Sunset Charge for a freshly-summoned minion whose first turn has
 * ended. SUPPRESSES the printed keyword (`_suppressedKeywords`) instead
 * of stripping `_printedKeywords`: suppression is server state that the
 * mirror fold carries to BOTH clients (stripping was a local mutation
 * the opponent never saw, so their CHG badge showed forever), and the
 * leave-play reset clears suppression, so a re-entering copy gets its
 * badge back (the strip survived death → recast and killed it for good).
 * Trade-off: suppression also masks a LATER aura grant's badge on this
 * card, but post-boundary Charge is inert either way (sickness is gone).
 */
export function expireFreshCharge(card) {
  if (!card) return;
  if (!card._chargeFresh) return;
  delete card._chargeFresh;
  const suppressed = card._suppressedKeywords || [];
  if ((card._printedKeywords || []).includes('charge') && !suppressed.includes('charge')) {
    card._suppressedKeywords = [...suppressed, 'charge'];
  }
}

/**
 * Charge granted AFTER summon. A manual radial-menu toggle, an aura
 * coming into range, or an effect like Evil Presence. Must clear the
 * sickness flag markSummonedThisTurn already set. markSummonedThisTurn
 * only skips the flag when Charge is present at summon time; the tap
 * gate (blocksCostTap) reads the flag alone and never re-checks Charge,
 * so a late grant has no game-effect until the flag is removed here.
 * No-op (returns false) when the minion was not sick.
 */
export function applyLateCharge(card) {
  if (!card || !card._hasSummoningSickness) return false;
  delete card._hasSummoningSickness;
  return true;
}

/**
 * Predicate consumed by GameBoard._applyTap: should this tap be
 * refused because the card has summoning sickness AND the tap is
 * a cost-paying source? Effect-driven taps and user-override taps
 * pass through.
 */
export function blocksCostTap(card, source) {
  if (!hasSummoningSickness(card)) return false;
  return COST_PAYING_TAP_SOURCES.has(source);
}
